import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";

import { motion} from "framer-motion";
import RevealSection from "./layout/RevealSection";
import { BRAND_BG, GRADIENT_BAR, PARTICLE_COUNT, bodyCls, labelCls } from "../constants/constants";

const ParticleCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animId;
    let frame = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.2 + 0.3,
      speed: Math.random() * 0.00012 + 0.00004,
      opacity: Math.random() * 0.14 + 0.03,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      for (const p of particles) {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
};




const Hairline = () => (
  <div className="w-full h-px bg-[#BC3F4A]/10" aria-hidden="true" />
);


const Hero = () => (
  <div
    className={`relative min-h-[52vh] md:mt-20 bg-[${BRAND_BG}] flex flex-col items-center justify-center px-6 pt-36 pb-20 overflow-hidden`}
  >
    <div className={`${GRADIENT_BAR} top-0`} aria-hidden="true" />
    <ParticleCanvas />

    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative z-10 max-w-xl w-full text-center"
    >
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`${labelCls} mb-5`}
      >
        About
      </motion.p>

      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.7 }}
        className="font-playfair text-4xl sm:text-5xl text-[#1c1917] leading-tight mb-6"
        style={{ textWrap: "balance" }}
      >
        Made by hand.
        <br />
        <span className="text-[#BC3F4A] italic">Meant for something.</span>
      </motion.h1>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="w-10 h-px bg-[#BC3F4A]/40 mx-auto mt-8"
        aria-hidden="true"
      />
    </motion.div>

  </div>
);


const StorySection = () => (
  <RevealSection delay={0.05}>
    <div className={`${bodyCls} space-y-5`}>
      <p>
        I learned string art at a summer camp in Crimea in 2003. We were given a
        list of workshops to choose from. By the time it was my turn, not many
        were left — string art was one of them.
      </p>
      <p>
        We started with simple punched cardstock designs and worked toward more
        complex patterns. I was absorbed. At the end, the instructor said she
        would give a certificate for exceptional effort to only a handful of
        students. I was one of them.
      </p>
      <p>
        That was the first time I understood I could make something worth
        giving.
      </p>
      {/* <p>
        Today Crimea is illegally occupied by russia. The place where I learned
        this craft no longer belongs to Ukraine. That is part of why this shop
        exists.
      </p> */}
      <p>
        The embroidery came from an art class — a whole semester spent
        sewing a <em>rushnyk</em>, a traditional Easter towel. It still covers our family's Easter basket every
        year.
      </p>
      <p>
        The photography came from a darkroom, where I could lose hours
        developing film and deciding which negatives were worth enlarging.
      </p>
      <p>
        I also built and maintain this site — steadily, alongside everything
        else here.
      </p>
      <p>
        Each card is handmade. 70% of every purchase goes directly to people in
        Ukraine — people I know and trust.
      </p>
      <p>
        Everyone has something to offer — a skill, a craft, some time. It
        doesn't have to be large to be real.
      </p>
      <p className="font-playfair italic text-[#1c1917] text-lg leading-snug">
        Find what you can do. Then do it.
      </p>
    </div>
  </RevealSection>
);




// ─── Tagline ──────────────────────────────────────────────────────────────
const Tagline = () => (
  <RevealSection delay={0.1} className="text-center pt-2">
    <div className="w-8 h-px bg-[#BC3F4A]/30 mx-auto mb-6" aria-hidden="true" />
    <p className={`${labelCls} tracking-[0.25em]`}>Make it. Mean it.</p>
  </RevealSection>
);


const About = () => (
  <>
    <Helmet>
      <title>About · Sanguine Accent</title>
      <meta
        name="description"
        content="Handmade string art cards rooted in Ukraine — and 70% of every purchase going straight to people serving there."
      />
    </Helmet>

    <Hero />

    <main className="bg-[#f5f3ea] px-6 pb-24">
      <div className="max-w-xl mx-auto space-y-14">
        <StorySection />
        <Hairline />
        <Tagline />
      </div>
    </main>
  </>
);

export default About;
